import { useEffect, useRef, useState } from "react"
import {
    AlmacenArticulo,
    Order,
    TipoOrdenamiento,
    useTableArticulosLazyQuery,
} from "src/gql/schema"
import { useProfile } from "src/shared/hooks/useProfile"
import { FilterSortState } from ".."
import { TODOS_ALMACEN_FILTER } from "../constants/mosaic-filters"
import { resolveAlmacenId, resolveDisponibilidadFilters, resolveTipoArticulo } from "./resolveArticulosVariables"

const useArticulosTableQuery = ({
    page,
    almacenSelected,
    almacenes,
    filterSortState,
    disabled
}: {
    page: number
    almacenSelected: string
    almacenes: string[]
    filterSortState?: FilterSortState,
    disabled: boolean
}) => {
    const [getArticulos] = useTableArticulosLazyQuery()
    const { hotel_id } = useProfile()
    const abortControllerRef = useRef<AbortController | null>(null)
    const filterRequestIdRef = useRef(0)

    const [isLoading, setisLoading] = useState(true)
    const [articulos, setarticulos] = useState<{
        articulos: AlmacenArticulo[];
        totalPages: number;
        currentPage: number;
    } | null>(null)

    useEffect(() => {
        setarticulos(null)
    }, [disabled])
    
    
    useEffect(() => {
        if (!almacenes.length || !almacenSelected || disabled) {
            return
        }
        setisLoading(true)
        const controller = new AbortController()
        const currentRequestId = ++filterRequestIdRef.current
        const {
            filterEstadoArticulo,
            filterEstadoAlmacenArticulo,
        } = resolveDisponibilidadFilters({filterSortState})

        const variables = {
            hotel_id,

            almacen_id: resolveAlmacenId({almacenes, almacenSelected, filterSortState}),

            pagination_options: {
                take: 12,
                page,
                // key siempre presente
                order: filterSortState?.nameSort?.sort ? undefined : Order.Asc,
            },

            filter_tipo_articulo: resolveTipoArticulo({filterSortState}),

            // key siempre presente
            nombreArticuloFilterOrder: filterSortState?.nameSort?.sort
                ? filterSortState.nameSort.sort === "up"
                    ? TipoOrdenamiento.Asc
                    : TipoOrdenamiento.Desc
                : undefined,

            // keys siempre presentes
            filterEstadoArticulo,
            filterEstadoAlmacenArticulo,
        }
        getArticulos({
            context: {
                fetchOptions: {
                    signal: controller.signal
                }
            },
            variables
        }).then((data) => {
            if (currentRequestId !== filterRequestIdRef.current) return
            if (!data.data?.almacenes_articulos) {
                return
            }
            setarticulos({
                currentPage: data.data.almacenes_articulos.paginacion.pagina_actual,
                totalPages: data.data.almacenes_articulos.paginacion.total_paginas,
                articulos: (data.data?.almacenes_articulos.almacenes_articulos as AlmacenArticulo[]) || [],
            })
            setisLoading(false)
        })

        return () => {
            controller.abort()
        }
    }, [page, almacenes, filterSortState, disabled])

    useEffect(() => {
        if (!almacenes.length || !almacenSelected || disabled) {
            return
        }
        setisLoading(true)
        const controller = new AbortController()
        getArticulos({
            context: {
                fetchOptions: {
                    signal: controller.signal  
                }
            },
            variables: {
                hotel_id,
                almacen_id: almacenSelected === TODOS_ALMACEN_FILTER.label ? almacenes : [almacenSelected],
                filter_tipo_articulo: resolveTipoArticulo({filterSortState}),
                pagination_options: {
                    take: 12,
                    page: 1,
                    order: Order.Asc,
                },
            },
        }).then((data) => {
            if (!data.data?.almacenes_articulos) {
                return
            }
            setarticulos({
                currentPage: data.data.almacenes_articulos.paginacion.pagina_actual,
                totalPages: data.data.almacenes_articulos.paginacion.total_paginas,
                articulos: (data.data?.almacenes_articulos.almacenes_articulos as AlmacenArticulo[]) || [],
            })
            setisLoading(false)
        })

        return () => {
            controller.abort()
        }
    }, [almacenSelected, almacenes])

    const refecth = async ({ almacenSelected, page, almacenes_ }: { almacenSelected: string; page: number, almacenes_: string[] }) => {
        setisLoading(true)
        abortControllerRef.current?.abort()
        return await getArticulos({
            variables: {
                hotel_id,
                filter_tipo_articulo: resolveTipoArticulo({filterSortState}),
                almacen_id: almacenSelected === TODOS_ALMACEN_FILTER.label ? almacenes_ : [almacenSelected],
                pagination_options: {
                    take: 12,
                    page,
                    order: Order.Asc,
                },
            },
        }).then((data) => {
            if (!data.data?.almacenes_articulos) {
                return
            }
            setarticulos({
                currentPage: data.data.almacenes_articulos.paginacion.pagina_actual,
                totalPages: data.data.almacenes_articulos.paginacion.total_paginas,
                articulos: (data.data?.almacenes_articulos.almacenes_articulos as AlmacenArticulo[]) || [],
            })
            setisLoading(false)
        })
    }

    return {
        articulos: articulos?.articulos,
        refecth,
        currentPage: articulos?.currentPage,
        totalPages: articulos?.totalPages,
        isLoading,
    }
}

export default useArticulosTableQuery
